// assets/b-card.js (#508): what b-product-card's ported parts do in the browser, with no Kalles
// call (Lee, 2026-10-15). Loaded by the blocks that need it (add to cart, wishlist, swatches,
// countdown); a module runs once however many cards load it. Listeners are delegated to the
// document, so cards that arrive by a filter, sorting or a page link work at once; the parts that
// show a state are set up again on b-collection's own update event.

const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

// ---------------------------------------------------------------- add to cart
// One request: /cart/add.js with sections=mini_cart returns the theme's drawer section already
// rendered with the new item (Shopify's Section Rendering API), the way the theme's own add does.
// The drawer's parts and the cart counts are refreshed from it, then the drawer opens. (Before,
// add, drawer section and /cart.js were three requests in a row: Lee, 2026-10-15, "a bit of a delay".)
// Adds queue, so the drawer always ends on the last state.
const DRAWER_PARTS = ['[data-cart-items]', '[data-cart-prices]', '[data-cart-tt-price]', '[data-cart-calc-shipping]', '[data-cart-discounts]'];
let cartQueue = Promise.resolve();

function refreshDrawer(html, countHtml) {
  const fresh = new DOMParser().parseFromString(html || '', 'text/html');
  const drawer = document.getElementById('t4s-mini_cart');
  if (drawer) {
    for (const sel of DRAWER_PARTS) {
      const from = fresh.querySelector(sel);
      drawer.querySelectorAll(sel).forEach((to) => { if (from) to.innerHTML = from.innerHTML; });
    }
  }
  // The item count comes from sections/b-cart-count, rendered in the same reply: the drawer's own
  // data-ccount is only there in the theme editor (sections/mini_cart.liquid), #508 code r1 finding 2.
  const countEl = new DOMParser().parseFromString(countHtml || '', 'text/html').querySelector('[data-b-cart-count]');
  const count = countEl ? Number(countEl.getAttribute('data-b-cart-count')) : NaN;
  if (!Number.isNaN(count)) {
    if (drawer) drawer.dataset.ccount = count;
    document.querySelectorAll('[data-cart-count], [data-cart-ttcount]').forEach((el) => { el.textContent = count; });
    // While <html> has t4s-cart-count-0 the drawer's checkout footer can't be used (mini-cart.css).
    document.documentElement.classList.toggle('t4s-cart-count-0', count === 0);
    document.dispatchEvent(new CustomEvent('cart:update:count', { bubbles: true, detail: { count } }));
  }
  // The header's cart total (snippets/menu-header-icons.liquid) isn't in the drawer section either; it
  // comes formatted from sections/b-cart-count too (code review round 1, finding 3).
  const totalText = countEl ? countEl.getAttribute('data-b-cart-total') : null;
  if (totalText !== null) document.querySelectorAll('[data-cart-tt-price]').forEach((el) => { el.textContent = totalText; });
}

// Opens the theme's own cart drawer (#t4s-mini_cart) the way the theme does, without calling it:
// shown with aria-hidden="false" (drawer.min.css), the page overlay shown, page scroll locked.
// Clicking the header's cart link instead fell through to the cart page when the theme skipped
// the click (Lee, 2026-10-15), so the drawer is opened directly. It closes on the overlay, its
// close buttons and Escape.
const LOCK = ['t4s-lock-scroll', 'is--opend-drawer'];
function closeDrawer() {
  const drawer = document.getElementById('t4s-mini_cart');
  if (!drawer || drawer.getAttribute('aria-hidden') !== 'false') return;
  drawer.setAttribute('aria-hidden', 'true');
  document.querySelectorAll('.t4s-close-overlay').forEach((el) => el.classList.remove('is--visible'));
  document.body.classList.remove(...LOCK);
  document.documentElement.style.marginRight = '';
  document.body.style.overflow = '';
  if (drawer.bReturnFocus) drawer.bReturnFocus.focus();
}
function openDrawer(from) {
  const drawer = document.getElementById('t4s-mini_cart');
  if (!drawer) return false;
  drawer.classList.remove('t4s-dn');
  drawer.setAttribute('aria-hidden', 'false');
  drawer.bReturnFocus = from || null;
  document.querySelectorAll('.t4s-close-overlay').forEach((el) => el.classList.add('is--visible'));
  const scrollbar = window.innerWidth - document.documentElement.clientWidth;
  document.body.classList.add(...LOCK);
  if (scrollbar > 0) document.documentElement.style.marginRight = `${scrollbar}px`;
  document.body.style.overflow = 'hidden';
  const focusable = drawer.querySelector('[data-drawer-close], button, a[href]');
  if (focusable) focusable.focus({ preventScroll: true });
  return true;
}
document.addEventListener('click', (event) => {
  const drawer = document.getElementById('t4s-mini_cart');
  if (!drawer || drawer.getAttribute('aria-hidden') !== 'false') return;
  if (event.target.closest('.t4s-close-overlay') || event.target.closest('#t4s-mini_cart [data-drawer-close]')) closeDrawer();
});
document.addEventListener('keyup', (event) => { if (event.key === 'Escape') closeDrawer(); });

function afterAdd(form) {
  // Theme settings > Cart > after_action_atc: 0 stay, 3 open the drawer, 4 cart page, 5 checkout.
  const after = form.dataset.after || '3';
  if (after === '4') return window.location.assign(`${root}cart`);
  if (after === '5') return window.location.assign(`${root}checkout`);
  if (after !== '3') return;
  if (!openDrawer(form.querySelector('button'))) window.location.assign(`${root}cart`);
}

// The drawer, counts and total from the cart's real state, without adding anything (after an add
// whose reply couldn't be read or lacked a section). True when both sections came back.
async function syncCart() {
  try {
    const res = await fetch(`${root}?sections=mini_cart,b-cart-count`, { headers: { Accept: 'application/json' } });
    const data = await res.json();
    if (data && data.mini_cart && data['b-cart-count']) {
      refreshDrawer(data.mini_cart, data['b-cart-count']);
      return true;
    }
  } catch (error) {
    // Reported by the caller.
  }
  return false;
}

// A message in the card's status line with a link to the cart page, where the shopper can see what
// is really in the cart.
function statusWithCartLink(status, message) {
  if (!status) return;
  const link = document.createElement('a');
  link.href = `${root}cart`;
  link.textContent = 'View your cart';
  status.replaceChildren(`${message} `, link);
}

document.addEventListener('submit', (event) => {
  const form = event.target.closest && event.target.closest('form[data-b-atc]');
  if (!form) return;
  event.preventDefault();
  const button = form.querySelector('button[type="submit"]');
  const status = form.querySelector('[data-b-atc-status]');
  // Keep the panel owner before an await: compare can rebuild its table while the add is pending.
  const compareDialog = form.closest('b-compare [data-dialog]');
  if (button.getAttribute('aria-busy') === 'true') return;
  button.setAttribute('aria-busy', 'true');
  if (status) status.textContent = '';
  cartQueue = cartQueue.then(async () => {
    try {
      const body = new FormData(form);
      body.append('sections', 'mini_cart,b-cart-count');
      body.append('sections_url', window.location.pathname);
      const res = await fetch(`${root}cart/add.js`, { method: 'POST', body, headers: { Accept: 'application/json' } });
      const data = await res.json();
      if (!res.ok) {
        // The store's own message (sold out meanwhile, a quantity limit); the drawer stays shut.
        if (status) status.textContent = data.description || data.message || 'This product could not be added.';
        return;
      }
      const sections = data.sections;
      if (!sections || !sections.mini_cart || !sections['b-cart-count']) {
        // Added, but the reply lacks the drawer or the count (code review round 1, finding 2): re-read
        // the cart rather than open a stale drawer, and never send the add again.
        if (!(await syncCart())) { statusWithCartLink(status, 'Added to your cart.'); return; }
      } else {
        refreshDrawer(sections.mini_cart, sections['b-cart-count']);
      }
      // The add worked: a form inside a panel (the compare panel, #730) hears it first and closes, so the
      // drawer that opens next isn't under it. A failed add keeps its message where it was.
      form.dispatchEvent(new CustomEvent('b-card:added', { bubbles: true }));
      if (!form.isConnected && compareDialog && compareDialog.isConnected) {
        compareDialog.dispatchEvent(new CustomEvent('b-card:added', { bubbles: true }));
      }
      afterAdd(form);
    } catch (error) {
      // A cut-off or unreadable response may already have added the item, so the form is never sent
      // again (it could add twice): say so, and show the cart as it really is.
      await syncCart();
      statusWithCartLink(status, 'We could not confirm this was added.');
    } finally {
      button.removeAttribute('aria-busy');
    }
  });
});

// ---------------------------------------------------------------- wishlist
// The theme's local wishlist: localStorage.t4s_wis, "id:<product id>" values, newest first, unique,
// at most 50, as the theme writes it, so its wishlist page and header count keep working. The
// header's wishlist link is answered here first (capture phase) from the current list, because the
// theme builds that link once when the page loads.
const WIS_KEY = 't4s_wis';
const WIS_MAX = 50;
const readWishlist = () => { try { return (localStorage.getItem(WIS_KEY) || '').split(',').filter(Boolean); } catch { return []; } };
const writeWishlist = (list) => { try { localStorage.setItem(WIS_KEY, list.join(',')); } catch {} };
const wishlistUrl = (list) => `${root}search/?view=wishlist&type=product&options[unavailable_products]=last&q=${encodeURI(list.join(' OR '))}`;

function paintWishlist(scope = document) {
  const list = readWishlist();
  scope.querySelectorAll('[data-b-wishlist]').forEach((button) => {
    const on = list.includes(`id:${button.dataset.bWishlist}`);
    button.setAttribute('aria-pressed', on ? 'true' : 'false');
    const label = button.querySelector('[data-b-wishlist-label]');
    if (label) label.textContent = on ? button.dataset.labelRemove : button.dataset.labelAdd;
  });
  document.querySelectorAll('[data-count-wishlist]').forEach((el) => { el.textContent = list.length; });
}

document.addEventListener('click', (event) => {
  const button = event.target.closest && event.target.closest('[data-b-wishlist]');
  if (!button) return;
  event.preventDefault();
  const id = `id:${button.dataset.bWishlist}`;
  let list = readWishlist().filter((x) => x !== id);
  if (button.getAttribute('aria-pressed') !== 'true') list.unshift(id);
  writeWishlist(list.slice(0, WIS_MAX));
  paintWishlist();
});

document.addEventListener('click', (event) => {
  const link = event.target.closest && event.target.closest('[data-link-wishlist]');
  if (!link) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  // From the current list even when it's empty: the theme's link still lists what was removed.
  window.location.assign(wishlistUrl(readWishlist()));
}, true);

// ---------------------------------------------------------------- swatches
// Hovering or focusing a swatch shows its variant's image on the card; leaving the swatches puts
// the product image back. With Theme settings' swatch limit at 0 ("responsive auto number"), as
// many swatches as fit on one line show, then "+X".
function cardImage(swatch) {
  const card = swatch.closest('.b-card');
  return card && card.querySelector('img[data-b-card-img]');
}
function showSwatch(swatch) {
  const img = cardImage(swatch);
  if (!img || !swatch.dataset.bSwatchImg) return;
  // The card's second image shows on hover and would cover the preview (PR 589 code r1, finding 2).
  swatch.closest('.b-card').classList.add('b-card--swatch-preview');
  if (!img.dataset.bOrigSrc) { img.dataset.bOrigSrc = img.getAttribute('src') || ''; img.dataset.bOrigSrcset = img.getAttribute('srcset') || ''; }
  img.setAttribute('src', swatch.dataset.bSwatchImg);
  if (swatch.dataset.bSwatchSrcset) img.setAttribute('srcset', swatch.dataset.bSwatchSrcset);
}
function restoreImage(container) {
  const card = container.closest('.b-card');
  if (card) card.classList.remove('b-card--swatch-preview');
  const img = cardImage(container);
  if (!img || !img.dataset.bOrigSrc) return;
  img.setAttribute('src', img.dataset.bOrigSrc);
  if (img.dataset.bOrigSrcset) img.setAttribute('srcset', img.dataset.bOrigSrcset);
}
for (const type of ['mouseover', 'focusin']) {
  document.addEventListener(type, (event) => {
    const swatch = event.target.closest && event.target.closest('.b-card__swatch[data-b-swatch-img]');
    if (swatch) showSwatch(swatch);
  });
}
for (const type of ['mouseout', 'focusout']) {
  document.addEventListener(type, (event) => {
    const box = event.target.closest && event.target.closest('.b-card__swatches');
    if (box && !box.contains(event.relatedTarget)) restoreImage(box);
  });
}

function fitSwatches(box) {
  const items = [...box.querySelectorAll('.b-card__swatch:not(.b-card__swatch--more):not([data-b-auto-more])')];
  items.forEach((el) => { el.hidden = false; });
  box.querySelectorAll('[data-b-auto-more]').forEach((el) => el.remove());
  if (!items.length) return;
  const top = items[0].offsetTop;
  const overflow = items.findIndex((el) => el.offsetTop > top);
  if (overflow < 0) return;
  // Room for "+X" on the first line: drop one more swatch.
  const keep = Math.max(1, overflow - 1);
  items.slice(keep).forEach((el) => { el.hidden = true; });
  const more = document.createElement('a');
  more.className = 'b-card__swatch b-card__swatch--more';
  more.dataset.bAutoMore = '';
  more.href = box.dataset.bSwatchUrl || '#';
  more.innerHTML = `+${items.length - keep}<span class="visually-hidden"> more colours</span>`;
  box.append(more);
}
const swatchObserver = 'ResizeObserver' in window ? new ResizeObserver((entries) => entries.forEach((e) => fitSwatches(e.target))) : null;

// ---------------------------------------------------------------- countdown
function tickCountdowns() {
  const now = Math.floor(Date.now() / 1000);
  document.querySelectorAll('[data-b-countdown]').forEach((el) => {
    const left = Number(el.dataset.bCountdown) - now;
    if (left <= 0) { el.hidden = true; return; }
    const d = Math.floor(left / 86400), h = Math.floor((left % 86400) / 3600), m = Math.floor((left % 3600) / 60), s = left % 60;
    const text = el.querySelector('[data-b-countdown-text]');
    if (text) text.textContent = `${d}d ${h}h ${m}m ${s}s`;
  });
}

// ---------------------------------------------------------------- set up, and again after updates
function setUp(scope = document) {
  paintWishlist(scope);
  scope.querySelectorAll('[data-b-swatch-auto]').forEach((box) => {
    fitSwatches(box);
    if (swatchObserver && !box.dataset.bObserved) { box.dataset.bObserved = ''; swatchObserver.observe(box); }
  });
  tickCountdowns();
}
setUp();
document.addEventListener('b-collection:update', () => setUp());
window.addEventListener('storage', (event) => { if (event.key === WIS_KEY) paintWishlist(); });
// One timer for every countdown on the page, started when the first one appears.
let ticking = null;
const startTicking = () => { if (!ticking && document.querySelector('[data-b-countdown]')) ticking = setInterval(tickCountdowns, 1000); };
startTicking();
document.addEventListener('b-collection:update', startTicking);
